// src/pages/MenuPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Skeleton,
  Fade,
  Zoom
} from '@mui/material';
import logo from '../assets/logo-sao-pedro.png';
import Lottie from 'lottie-react';
import busAnimation from '../assets/bus.json';
import IconButton from '@mui/material/IconButton';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BuildIcon from '@mui/icons-material/Build';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ComputerIcon from '@mui/icons-material/Computer';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useThemeMode } from '../context/ThemeContext';

const setores = [
   { label: 'Tráfego', route: '/trafego', icon: DirectionsBusIcon },
  { label: 'Compras', route: '/setor2', icon: ShoppingCartIcon },
  { label: 'Manutenção', route: '/setor3', icon: BuildIcon },
  { label: 'Arrecadação', route: '/setor4', icon: AccountBalanceIcon },
  { label: 'TI', route: '/setor5', icon: ComputerIcon },
  { label: 'RH', route: '/setor6', icon: PeopleAltIcon },
  { label: 'Estratégico', route: '/setor7', icon: TrendingUpIcon }
];

// quais "abas" (allowedTabs) habilitam cada card de setor no menu
const setorPermissions = {
  'Tráfego': ['operacao', 'penalizacao'],
  'Compras': ['setor2'],
  'Manutenção': ['manutencao'],
  'Arrecadação': ['setor4'],
  'TI': ['setor5'],
  'RH': ['setor6'],
  'Estratégico': ['setor7']
};


export default function MenuPage() {
  const navigate = useNavigate();
  const [openLogout, setOpenLogout] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navigating, setNavigating] = useState(false);
  const { mode, setMode } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Proteção e leitura segura do usuário da sessão
  useEffect(() => {
    const initUser = async () => {
      // Simular loading mínimo para UX
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const userData = sessionStorage.getItem('user');
      if (!userData) {
        navigate('/');
      } else {
        setUser(JSON.parse(userData));
      }
      setLoading(false);
    };
    
    initUser();
  }, [navigate]);

  const handleLogoutClick = () => setOpenLogout(true);
  const handleCancelLogout = () => setOpenLogout(false);
  
  const handleCardClick = async (route) => {
    setNavigating(true);
    // Pequeno delay para mostrar feedback visual
    await new Promise(resolve => setTimeout(resolve, 300));
    navigate(route);
  };
  const handleConfirmLogout = async () => {
    try {
      // Chama o endpoint de logout para limpar o cookie no servidor
      const API_BASE = process.env.REACT_APP_API_BASE ?? '';
      const API_KEY = process.env.REACT_APP_API_KEY;
      console.log('🔍 MenuPage logout - API_BASE (env):', process.env.REACT_APP_API_BASE);
      console.log('🔍 MenuPage logout - API_BASE (final):', API_BASE);
      
      const headers = { 'Content-Type': 'application/json' };
      if (API_KEY) headers['X-API-Key'] = API_KEY;
      
      await fetch(`${API_BASE}/api/logout`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
    } catch (err) {
      console.warn('Erro ao fazer logout no servidor:', err);
    }
    
    sessionStorage.clear();
    setOpenLogout(false);
    navigate('/');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme =>
            theme.palette.mode === 'light'
              ? theme.palette.background.default
              : '#1A202C'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Skeleton variant="rectangular" width={200} height={60} sx={{ mb: 4, mx: 'auto', borderRadius: 2 }} />
          <Grid container spacing={2} justifyContent="center">
            {[...Array(isMobile ? 2 : 4)].map((_, i) => (
              <Grid key={i} item>
                <Skeleton variant="rectangular" width={isMobile ? 140 : 180} height={isMobile ? 120 : 160} sx={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  if (!user) return null;

  const isMaster = user.allowedSector === 'all';
  const userTabs = Array.isArray(user.allowedTabs) ? user.allowedTabs : [];


  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        bgcolor: theme =>
          theme.palette.mode === 'light'
            ? theme.palette.background.default
            : '#1A202C',
        backgroundImage: theme =>
          theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, rgba(255,255,255,0.4), rgba(0,0,0,0))'
            : 'none',
        py: { xs: 4, sm: 6, md: 8 },
        px: { xs: 1, sm: 2 },
        pb: { xs: 12, sm: 14, md: 16 } // Espaço extra para o footer
      }}
    >
<Box
  sx={{
    position: 'fixed',
    top: { xs: '15px', sm: '22px' },
    right: { xs: '15px', sm: '20px' },
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    zIndex: 1000
  }}
>
    <IconButton
      onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
      color="inherit"
      aria-label="Alternar tema"
      size={isMobile ? 'small' : 'medium'}
      sx={{ 
        border: '1px solid', 
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.3s ease'
      }}
    >
      {mode === 'dark' ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
    </IconButton>

    <Button
      variant="outlined"
      color="primary"
      onClick={handleLogoutClick}
      aria-label="Sair do sistema"
      size={isMobile ? 'small' : 'medium'}
      sx={{
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: 2
        }
      }}
    >
      Sair
    </Button>
  </Box>


      {/* Diálogo de confirmação de logout */}
      <Dialog open={openLogout} onClose={handleCancelLogout}>
        <DialogTitle>Confirmar saída</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja sair?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout}>Não</Button>
          <Button onClick={handleConfirmLogout} autoFocus>
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logo removida do canto superior */}

      {/* Hero */}
      <Fade in timeout={800}>
        <Container
          maxWidth="md"
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 6 },
            mt: { xs: 8, sm: 4 },
            py: { xs: 3, sm: 4 },
            mx: { xs: 1, sm: 'auto' },
            backdropFilter: theme =>
              theme.palette.mode === 'light' ? 'blur(8px)' : 'none',
            bgcolor: theme =>
              theme.palette.mode === 'light'
                ? 'rgba(255,255,255,0.6)'
                : '#2D3748',
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: 3,
            transition: 'all 0.3s ease'
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Logo São Pedro"
            sx={{
              width: { xs: 200, sm: 250, md: 300 },
              height: 'auto',
              mb: 2,
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: theme => 
                theme.palette.mode === 'light' 
                  ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                  : 'brightness(1.2) contrast(1.3) saturate(1.2) hue-rotate(5deg) drop-shadow(0 0 30px rgba(33, 150, 243, 0.6)) drop-shadow(0 0 60px rgba(33, 150, 243, 0.3)) drop-shadow(0 8px 20px rgba(0,0,0,0.5))',
              '&:hover': {
                transform: 'scale(1.05) translateY(-2px)',
                filter: theme => 
                  theme.palette.mode === 'light' 
                    ? 'drop-shadow(0 8px 16px rgba(0,0,0,0.2)) brightness(1.05)'
                    : 'brightness(1.3) contrast(1.4) saturate(1.3) hue-rotate(10deg) drop-shadow(0 0 40px rgba(33, 150, 243, 0.8)) drop-shadow(0 0 80px rgba(33, 150, 243, 0.4)) drop-shadow(0 0 120px rgba(100, 181, 246, 0.2)) drop-shadow(0 12px 25px rgba(0,0,0,0.6))',
              },
              // Animação sutil de pulso no modo dark
              ...(theme.palette.mode === 'dark' && {
                animation: 'logoGlow 3s ease-in-out infinite alternate',
                '@keyframes logoGlow': {
                  '0%': {
                    filter: 'brightness(1.2) contrast(1.3) saturate(1.2) hue-rotate(5deg) drop-shadow(0 0 30px rgba(33, 150, 243, 0.6)) drop-shadow(0 0 60px rgba(33, 150, 243, 0.3)) drop-shadow(0 8px 20px rgba(0,0,0,0.5))'
                  },
                  '100%': {
                    filter: 'brightness(1.25) contrast(1.35) saturate(1.25) hue-rotate(8deg) drop-shadow(0 0 35px rgba(33, 150, 243, 0.7)) drop-shadow(0 0 70px rgba(33, 150, 243, 0.4)) drop-shadow(0 8px 20px rgba(0,0,0,0.5))'
                  }
                }
              })
            }}
          />
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            sx={{
              letterSpacing: '0.5px',
              color: theme =>
                theme.palette.mode === 'light' ? '#00C2C7' : '#FFFFFF',
              transition: 'all 0.3s ease'
            }}
          >
            Conectando você aos seus dados!
          </Typography>
        </Container>
      </Fade>

      {/* Grid de Cards */}
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, mb: { xs: 4, sm: 6, md: 8 } }}>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
          {setores.map((setor, idx) => {
            const acessoPermitido =
              isMaster ||
              (setorPermissions[setor.label] || []).some(tab => userTabs.includes(tab));
            const IconComponent = setor.icon;

            return (
              <Grid key={idx} item xs={6} sm={4} md={3} lg={12/7}>
                <Zoom in timeout={600 + idx * 100}>
                  <Card
                    sx={{
                      width: 180,
                      height: 150,
                      mx: 'auto',
                      bgcolor: theme =>
                        theme.palette.mode === 'light' ? '#2196F3' : '#3E4757',
                      opacity: acessoPermitido ? 1 : 0.6,
                      pointerEvents: acessoPermitido ? 'auto' : 'none',
                      boxShadow: acessoPermitido ? 6 : 2,
                      borderRadius: 4,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': acessoPermitido ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
                        zIndex: 1,
                        transition: 'opacity 0.3s ease',
                        opacity: 0
                      } : {},
                      '&:hover': acessoPermitido ? {
                        transform: 'translateY(-12px) scale(1.02)',
                        boxShadow: 12,
                        '&::before': {
                          opacity: 1
                        },
                        '& .card-icon': {
                          transform: 'scale(1.1) rotate(5deg)'
                        },
                        '& .card-text': {
                          transform: 'translateY(-2px)'
                        }
                      } : {},
                      '&:active': acessoPermitido ? {
                        transform: 'translateY(-8px) scale(0.98)'
                      } : {}
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleCardClick(setor.route)}
                      disabled={navigating || !acessoPermitido}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        p: 1.5,
                        position: 'relative',
                        zIndex: 2,
                        '&:focus-visible': {
                          outline: '2px solid #fff',
                          outlineOffset: '2px'
                        }
                      }}
                    >
                      <IconComponent
                        className="card-icon"
                        sx={{
                          fontSize: 36,
                          color: '#FFFFFF',
                          mb: 1,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          filter: acessoPermitido ? 'none' : 'grayscale(100%)'
                        }}
                      />
                      <Typography
                        className="card-text"
                        variant="h6"
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          letterSpacing: '0.25px',
                          color: '#FFFFFF',
                          fontSize: '1rem',
                          lineHeight: 1.2,
                          transition: 'all 0.3s ease',
                          opacity: acessoPermitido ? 1 : 0.8
                        }}
                      >
                        {setor.label}
                      </Typography>
                      {!acessoPermitido && (
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            right: 8,
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '0.65rem'
                          }}
                        >
                          🔒
                        </Typography>
                      )}
                    </CardActionArea>
                  </Card>
                </Zoom>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Rodapé com animação de ônibus */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          height: { xs: 80, sm: 100, md: 120 },
          bgcolor: theme =>
            theme.palette.mode === 'light'
              ? theme.palette.background.default
              : '#1A202C',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          opacity: 0.8,
          zIndex: 0 // Garantir que fique atrás dos cards
        }}
      >
        <Box sx={{ 
          width: { xs: 200, sm: 250, md: 300 }, 
          height: { xs: 80, sm: 100, md: 120 },
          transition: 'all 0.3s ease'
        }}>
          <Lottie
            animationData={busAnimation}
            loop
            autoplay
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
      </Box>
    </Box>
  );
}